import { useSession } from "@/compat/next-auth-react";
import { useAdminView } from "@/components/rent/core/use-admin-view";
import { cn } from "@/lib/utils";

/**
 * Admin-only toggle that switches Rent list views between
 * "Just mine" (user-scoped) and "All owners" (admin scope).
 *
 * State persists in localStorage via `useAdminView`. Toggling reloads the
 * page so all in-flight queries re-fetch with the new scope.
 *
 * Hidden entirely for non-ADMIN users.
 */
export function AdminViewToggle() {
  const { data: session, status } = useSession();
  const { enabled, setEnabled } = useAdminView();

  if (status !== "authenticated") return null;
  if (session?.user?.role !== "ADMIN") return null;

  return (
    <button
      type="button"
      onClick={() => {
        setEnabled(!enabled);
        if (typeof window !== "undefined") window.location.reload();
      }}
      className={cn(
        "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition",
        enabled
          ? "bg-[var(--cp-accent)] text-white hover:brightness-110"
          : "bg-[var(--cp-surface-2)] text-[var(--cp-ink-2)] hover:bg-[var(--cp-line-2)]",
      )}
      title="Toggle admin view across all owners"
      aria-pressed={enabled}
    >
      <span
        className={cn(
          "h-2 w-2 rounded-full",
          enabled ? "bg-white" : "bg-[var(--cp-ink-3)]",
        )}
      />
      {enabled ? "All owners" : "Just mine"}
    </button>
  );
}

/**
 * Slim banner shown when the admin-view toggle is ON. Lives below the
 * RentSubNav header; click the dismiss button to switch back to "Just mine"
 * (which also reloads the page so queries refetch).
 */
export function AdminViewBanner() {
  const { data: session, status } = useSession();
  const { enabled, setEnabled } = useAdminView();

  if (status !== "authenticated") return null;
  if (session?.user?.role !== "ADMIN") return null;
  if (!enabled) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--cp-line)] bg-[var(--cp-accent-wash)] px-4 py-2 text-sm text-[var(--cp-accent-ink)] md:px-6">
      <span>
        Viewing data across <strong>all owners</strong> — admin mode
      </span>
      <button
        type="button"
        onClick={() => {
          setEnabled(false);
          if (typeof window !== "undefined") window.location.reload();
        }}
        className="rounded px-2 py-0.5 text-xs font-medium hover:bg-[var(--cp-surface)]/60"
      >
        Switch to Just Mine
      </button>
    </div>
  );
}
